// PromptGenerator/PromptLayout.tsx (Refactored)

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb, faPlus } from '@fortawesome/free-solid-svg-icons';
import styles from './PromptLayout.module.css';
import { StepIndicatorNames } from '../../types';

// Import Hooks and Components
import PromptGenerator from './PromptGenerator';
import VerticalNav from '../Home/VerticalNav';
import Navigation from '../Home/Navigation';
import PromptSidebar from './components/PromptSidebar';
import PromptViewer from './components/PromptViewer';
import DeleteAllModal from './components/DeleteAllModal';
import BatchProcessModal from './components/BatchProcessModal';

// Data/Action Hooks
import { useProjectData, PromptData } from './hooks/useProjectData';
import { usePromptActions } from './hooks/usePromptActions';

const PromptLayout: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for Modals
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);

  // --- HOOKS: Data Layer ---
  const { project, prompts, loading, setProject, setPrompts, loadPrompts } = useProjectData(projectId);
  
  // --- HOOKS: Action Layer (must be below data layer hooks) ---
  const {
    showPromptGenerator, setShowPromptGenerator, 
    selectedPromptId, setSelectedPromptId, // Use local setter here
    selectedPrompt, setSelectedPrompt, deletingAll, showDeleteAllConfirm,
    setShowDeleteAllConfirm, 
    handleNewPrompt, selectPrompt, handlePromptCreated, handleRegeneratePrompt, handleGenerateImage,
    deletePrompt, confirmDeleteAllPrompts, handleNavigateToProject
  } = usePromptActions(projectId, prompts, loadPrompts, setPrompts); // Removed redundant parameter

  // Update logic for prompt list when a prompt is edited/saved in the viewer
  const updatePromptsList = useCallback((updatedPrompt: PromptData) => {
    // 1. Update the list of all prompts (used by filters/sidebar)
    setPrompts(prevPrompts => prevPrompts.map(p => 
      p.id === updatedPrompt.id ? updatedPrompt : p
    ));
    // 2. Update the currently selected prompt instance (used by PromptViewer)
    setSelectedPrompt(updatedPrompt);
  }, [setPrompts, setSelectedPrompt]);

  // --- Vertical Navigation Handler (omitted for brevity) ---
  const handleNavSelected = useCallback((key: StepIndicatorNames) => {
    console.log('🔀 Navigating to step:', key);
    if (!projectId) return;
    if (key === StepIndicatorNames.ScriptGeneration) {
      console.log('➡️ Navigating to script Generator tab');
      navigate(`/script-generator/${projectId}/script-generator`);
    } else {
      // Navigate to project main page for other steps
      navigate(`/`);
    }
  }, [projectId, navigate]);
  
  // --- Effects for Auto-select on Initial Load ---
  useEffect(() => {
    const isBaseProjectRoute = location.pathname.match(/^\/prompt-generator\/[^\/]+\/?$/);
    
    // Redirect base URL to generator tab
    if (isBaseProjectRoute && projectId) {
      navigate(`/prompt-generator/${projectId}/prompt-generator`, { replace: true });
      return;
    }
    
    // If we have prompts and no selection, auto-select the first non-draft.
    // IMPORTANT: Only auto-select if the generator is NOT already showing (prevents interference with "New Prompt" action)
    if (prompts.length > 0 && !selectedPrompt && !showPromptGenerator) {
      const firstNonDraftPrompt = prompts.find(p => p.generatedPrompt && p.generatedPrompt.trim() !== '');
      if (firstNonDraftPrompt) {
        setSelectedPrompt(firstNonDraftPrompt);
        setSelectedPromptId(firstNonDraftPrompt.id);
        setShowPromptGenerator(false);
      }
    }
  }, [prompts, projectId, selectedPrompt, showPromptGenerator, navigate, setSelectedPrompt, setSelectedPromptId, setShowPromptGenerator]);

  
  // --- Render ---

  if (!projectId) return null;

  return (
    <div className={styles.pageContainer}>
      <Navigation />
      <VerticalNav 
        handleNavSelected={handleNavSelected} 
        initialStep={StepIndicatorNames.IdeaCreation}
        projectData={project}
      />
      <div className={styles.layout}>
        {/* Sidebar */}
        <PromptSidebar
          project={project}
          prompts={prompts}
          loading={loading}
          setProject={setProject}
          setPrompts={setPrompts}
          loadPrompts={loadPrompts}
          selectedPromptId={selectedPromptId}
          deletingPromptId={deletingAll ? 'ALL' : null}
          handleNewPrompt={handleNewPrompt}
          selectPrompt={selectPrompt}
          deletePrompt={deletePrompt}
          handleNavigateToProject={handleNavigateToProject}
          deletingAll={deletingAll}
          setShowDeleteAllConfirm={setShowDeleteAllConfirm}
          setIsBatchModalOpen={setIsBatchModalOpen}
        />
        
        {/* Main Content */}
        <div className={styles.mainContent}>
          {showPromptGenerator ? (
            <PromptGenerator 
              key={projectId + '-generator-' + selectedPromptId}
              onPromptCreated={handlePromptCreated}
              onNavigateToProject={handleNavigateToProject}
              projectId={projectId}
            />
          ) : selectedPrompt ? (
            <PromptViewer 
              selectedPrompt={selectedPrompt} 
              loadPrompts={loadPrompts} 
              updatePromptsList={updatePromptsList}
              handleRegeneratePrompt={handleRegeneratePrompt}
              handleGenerateImage={handleGenerateImage}
            />
          ) : (
            <div className={styles.emptyState}>
              <FontAwesomeIcon icon={faLightbulb} size="3x" className={styles.emptyIcon} />
              <h2>Welcome to {project?.title || 'Your Project'}</h2>
              <p>Create prompts for your project or select an existing one from the sidebar.</p>
              <Button
                className={styles.getStartedButton}
                onClick={handleNewPrompt}
                leftSection={<FontAwesomeIcon icon={faPlus} />}
                size="lg"
              >
                Create Your First Prompt
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Delete All Confirmation Dialog */}
      <DeleteAllModal
        isOpen={showDeleteAllConfirm}
        onClose={() => setShowDeleteAllConfirm(false)}
        promptsCount={prompts.length}
        onConfirm={() => confirmDeleteAllPrompts(projectId)}
        isDeleting={deletingAll}
      />

      {/* Batch Processing Modal */}
      <BatchProcessModal
        isOpen={isBatchModalOpen}
        onClose={() => setIsBatchModalOpen(false)}
        onComplete={loadPrompts}
        projectId={projectId || ''}
      />
    </div>
  );
};

export default PromptLayout;